import i18n from 'common/util/internationalization'
import {
    additionalHeadersDocuService,
    additionalHeadersWithJsonDocuService,
    TemplateDocuServiceBaseUrl
} from 'common/util/requestUtils'
import emitter from 'common/util/events'
import { removeSessionStorageItem } from 'businesslayer/minutesLocalStore'

// Helper function to handle 401 errors consistently with minutes-api
const handleTemplateServiceError = (response: Response) => {
    if (response.status === 401) {
        const message = i18n.t('SESSION_EXPIRED_MESSAGE')
        removeSessionStorageItem('selectedStatusFilter')
        removeSessionStorageItem('selectedCommitteeFilter')
        const url = new URL(window.location.href)
        url.search = ''
        window.history.replaceState(null, '', url.toString())
        emitter.emit('locked', { message })
        throw new Error('Authentication failed')
    }
}

export const fetchPreviewTemplatePDF = async (
    templateId: string
): Promise<{
    success: boolean
    images?: string[]
    pageCount?: number
    docxUrl?: string
    error?: string
    useDocxFallback: boolean
}> => {
    try {
        const imageEndpoint = `${TemplateDocuServiceBaseUrl}/templates/${templateId}/preview-template`
        const response = await fetch(imageEndpoint, {
            method: 'GET',
            headers: additionalHeadersWithJsonDocuService()
        })

        // Handle 401 errors consistently with minutes-api
        handleTemplateServiceError(response)

        if (response.status === 200) {
            const json = await response.json()
            if (json.images && Array.isArray(json.images)) {
                return {
                    success: true,
                    images: json.images,
                    pageCount: json.pageCount,
                    useDocxFallback: false
                }
            } else if (json.image) {
                return {
                    success: true,
                    images: [json.image],
                    pageCount: 1,
                    useDocxFallback: false
                }
            } else {
                throw new Error('No image(s) found in response')
            }
        } else if (response.status === 503) {
            // If image conversion is not available
            const errorText = await response.text()
            let errorMessage = 'Image conversion service unavailable'

            try {
                const jsonResponse = JSON.parse(errorText)
                errorMessage = jsonResponse.message
            } catch (error) {
                console.warn('Failed to parse error response:', error)
            }

            return {
                success: true,
                error: errorMessage,
                useDocxFallback: true
            }
        } else {
            throw new Error(`Failed to get template preview: ${response.status}`)
        }
    } catch (error: any) {
        console.error('Error fetching template preview:', error.message)
        return {
            success: false,
            error: error.message || 'Failed to load template preview',
            useDocxFallback: false
        }
    }
}

/**
 * Saves a template to the template service
 * @param formData - Form data containing template information and file
 * @returns Promise that resolves to success status and optional error message
 */
export const saveTemplateToServer = async (
    formData: FormData
): Promise<{
    success: boolean
    error?: string
    errorData?: unknown
    errorCode?: string
    validationErrors?: string[]
}> => {
    try {
        // Use the templates-api proxy endpoint to avoid CORS issues
        const response = await fetch(`${TemplateDocuServiceBaseUrl}/templates`, {
            method: 'POST',
            headers: additionalHeadersDocuService(),
            body: formData
        })

        // Handle 401 errors consistently with minutes-api
        handleTemplateServiceError(response)
        if (!response.ok) {
            const errorData = await response.json().catch(() => null)

            const errObj: any = errorData || {}
            const errorCode: string | undefined =
                errObj?.message || errObj?.code || errObj?.errorCode || undefined

            const collectToArray = (value: unknown): string[] => {
                if (!value) return []
                if (Array.isArray(value)) return value.map((v) => String(v))
                if (typeof value === 'string') return [value]
                return []
            }

            const validationErrors: string[] = [
                ...collectToArray(errObj?.errors),
                ...collectToArray(errObj?.errorMessages),
                ...collectToArray(
                    errObj?.Invalidtags || errObj?.invalidTags || errObj?.invalid_tags
                ).map((t) => t),
                ...(() => {
                    const supported = collectToArray(
                        errObj?.SupportedTags || errObj?.supportedTags || errObj?.supported_tags
                    )
                    return supported.length ? [`Supported tags: ${supported.join(', ')}`] : []
                })()
            ].filter(Boolean)

            return {
                success: false,
                error: errObj?.message || `Server responded with status ${response.status}`,
                errorData,
                errorCode,
                validationErrors
            }
        }

        return { success: true }
    } catch (error) {
        console.error('Error saving template:', (error as any).message)
        return {
            success: false,
            error: (error as any).message || 'Failed to save template'
        }
    }
}

/**
 * Updates an existing template metadata on the template service
 * @param templateId - ID of the template to update
 * @param templateData - Object containing updated template information
 * @returns Promise that resolves to success status and optional error message
 */
export const updateTemplateMetadata = async (
    templateId: string,
    templateData: {
        title: string
        description: string
        isDefault?: boolean
    }
): Promise<{ success: boolean; error?: string }> => {
    try {
        const response = await fetch(`${TemplateDocuServiceBaseUrl}/templates/${templateId}`, {
            method: 'PUT',
            headers: additionalHeadersWithJsonDocuService(),
            body: JSON.stringify(templateData)
        })

        // Handle 401 errors consistently with minutes-api
        handleTemplateServiceError(response)

        if (!response.ok) {
            const errorData = await response.json().catch(() => null)
            throw new Error(errorData?.message || `Server responded with status ${response.status}`)
        }

        return { success: true }
    } catch (error) {
        console.error('Error updating template:', error.message)
        return {
            success: false,
            error: error.message || 'Failed to update template'
        }
    }
}

export const updateSitePreferredTemplate = async (
    templateId: string,
    templateData: {
        title: string
        description: string
        isDefault?: boolean
    }
): Promise<{ success: boolean; error?: string }> => {
    try {
        const response = await fetch(`${TemplateDocuServiceBaseUrl}/templates/${templateId}`, {
            method: 'PATCH',
            headers: additionalHeadersWithJsonDocuService(),
            body: JSON.stringify(templateData)
        })

        // Handle 401 errors consistently with minutes-api
        handleTemplateServiceError(response)

        if (!response.ok) {
            const errorData = await response.json().catch(() => null)
            throw new Error(errorData?.message || `Server responded with status ${response.status}`)
        }

        return { success: true }
    } catch (error) {
        console.error('Error updating preferred template:', error.message)
        return {
            success: false,
            error: error.message || 'Failed to update preferred template'
        }
    }
}

export const fetchAllTemplates = async (page, sortBy, order, perPage) => {
    try {
        const response = await fetch(
            `${TemplateDocuServiceBaseUrl}/templates/?page=${page}&per_page=${perPage}&sort_by=${sortBy}&sort_order=${order}`,
            {
                method: 'GET',
                headers: additionalHeadersWithJsonDocuService()
            }
        )

        // Handle 401 errors consistently with minutes-api
        handleTemplateServiceError(response)

        if (!response.ok) {
            throw new Error(i18n.t('TEMPLATE_FETCH_ERROR'))
        }

        return await response.json()
    } catch (error) {
        console.error('Error fetching all templates:', error.message)
    }
}

export const deleteTemplate = async (templateId) => {
    try {
        const response = await fetch(`${TemplateDocuServiceBaseUrl}/templates/${templateId}`, {
            method: 'DELETE',
            headers: additionalHeadersWithJsonDocuService()
        })

        // Handle 401 errors consistently with minutes-api
        handleTemplateServiceError(response)

        if (!response.ok) {
            throw new Error('Failed to delete template')
        }
        return await response.json()
    } catch (error) {
        console.error('Error deleting template:', error.message)
    }
}

export const fetchUserPreferredTemplate = async () => {
    try {
        const response = await fetch(`${TemplateDocuServiceBaseUrl}/users/preferred-template`, {
            method: 'GET',
            headers: additionalHeadersWithJsonDocuService()
        })

        // Handle 401 errors consistently with minutes-api
        handleTemplateServiceError(response)

        if (!response.ok) {
            throw new Error(i18n.t('TEMPLATE_FETCH_ERROR'))
        }
        return await response.json()
    } catch (error) {
        console.error('Error fetching user preferred template:', error.message)
    }
}

export const setUserPreferredTemplate = async (
    preferredTemplateId: string | null,
    preferredExportOption: string | null
) => {
    try {
        const response = await fetch(`${TemplateDocuServiceBaseUrl}/users/preferred-template`, {
            method: 'PATCH',
            headers: additionalHeadersWithJsonDocuService(),
            body: JSON.stringify({
                preferredTemplateId: preferredTemplateId,
                preferredExportOption: preferredExportOption
            })
        })

        // Handle 401 errors consistently with minutes-api
        handleTemplateServiceError(response)

        if (!response.ok) {
            throw new Error(i18n.t('TEMPLATE_FETCH_ERROR'))
        }

        return await response.json()
    } catch (error) {
        console.error('Error setting user preferred template:', error.message)
    }
}

export const generateDocxFromTemplate = async ({
    templateId,
    minutesDocumentId,
    committeeId,
    withActions,
    apiToken,
    timezone
}: {
    templateId: string
    minutesDocumentId: number
    committeeId: number
    withActions: boolean
    apiToken: string
    timezone: string
}): Promise<{ success: boolean; response?: Response; error?: string }> => {
    try {
        const headers = {
            ...additionalHeadersWithJsonDocuService(),
            'X-Client-Token': `Bearer ${apiToken}`,
        }
        const body = JSON.stringify({
            templateId,
            minutesDocumentId,
            committeeId,
            withActions,
            timezone
        })
        const response = await fetch(`${TemplateDocuServiceBaseUrl}/templates/generate-docx`, {
            method: 'POST',
            headers,
            body
        })

        // Handle 401 errors consistently with minutes-api
        handleTemplateServiceError(response)

        if (!response.ok) {
            const errorData = await response.json().catch(() => null)
            console.error('Error:', errorData)
            throw new Error(i18n.t('FAILED_TO_GENERATE_DOCX_FILE'))
        }
        return { success: true, response }
    } catch (error) {
        console.error('Error:', error.message)
        return {
            success: false,
            error: error.message || 'Failed to generate DOCX'
        }
    }
}

export const downloadTemplate = async (templateId: string, templateTitle: string) => {
    try {
        const response = await fetch(
            `${TemplateDocuServiceBaseUrl}/templates/${templateId}/download`,
            {
                method: 'GET',
                headers: additionalHeadersWithJsonDocuService()
            }
        )

        // Handle 401 errors consistently with minutes-api
        handleTemplateServiceError(response)

        if (!response.ok) {
            throw new Error('Failed to download file')
        }

        const blob = await response.blob()
        const fileName = `${templateTitle}.docx`
        // Create a download link and click it
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        a.click()
        window.URL.revokeObjectURL(url)
        return { success: true }
    } catch (error) {
        console.error('File download error:', error)
        return { success: false, message: error.message }
    }
}
